import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, ShieldCheck, Sparkles, MapPin } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import AvailabilityWidget from "@/components/AvailabilityWidget";
import { imgSrc } from "@/lib/api";

const TRUST_ICONS = [ShieldCheck, Sparkles, MapPin];

export default function Hero({ hero }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);
  const lines = hero?.title_lines?.length ? hero.title_lines : ["Mystique Copper Villa"];

  return (
    <section ref={ref} className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <img
          src={imgSrc(hero?.image)}
          alt="Mystique Copper Villa at golden hour on the Karwar coast"
          className="h-full w-full animate-slow-zoom object-cover"
          fetchpriority="high"
        />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 -z-10 bg-charcoal" aria-hidden="true" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-charcoal via-transparent to-charcoal/40" aria-hidden="true" />

      <div className="mx-auto w-full max-w-7xl px-4 pb-10 pt-36 sm:px-8 lg:px-16">
        <motion.p
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}
          className="eyebrow mb-6" data-testid="hero-badge"
        >
          {hero?.badge}
        </motion.p>
        <h1 className="font-serif text-4xl leading-[1.08] tracking-tight text-cream sm:text-6xl lg:text-7xl">
          {lines.map((line, i) => (
            <MaskedLine key={i} delay={0.45 + i * 0.18} className={i === lines.length - 1 ? "italic text-copper-gradient" : ""}>
              {line}
            </MaskedLine>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-6 max-w-xl text-base font-light leading-relaxed text-cream/80 md:text-lg"
        >
          {hero?.subtitle}
        </motion.p>

        {hero?.trust?.length > 0 && (
          <motion.ul
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-6 flex flex-wrap gap-x-6 gap-y-2" data-testid="hero-trust-indicators"
          >
            {hero.trust.filter(Boolean).map((t, i) => {
              const Icon = TRUST_ICONS[i % TRUST_ICONS.length];
              return (
                <li key={i} className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-cream-muted">
                  <Icon className="h-3.5 w-3.5 text-copper-400" /> {t}
                </li>
              );
            })}
          </motion.ul>
        )}

        <motion.div
          initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.45, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 max-w-3xl"
        >
          <AvailabilityWidget />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
        className="pointer-events-none absolute bottom-6 right-8 hidden lg:block" aria-hidden="true"
      >
        <ChevronDown className="h-6 w-6 animate-bounce text-copper-400" />
      </motion.div>
    </section>
  );
}
